import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { CalendarRange, Users, Clock, Zap, Download, AlertTriangle, GitBranch, ChevronDown, Save } from "lucide-react";

const PX = 22;                 // pixels per calendar day
const ROW_H = 36;
const BAR_H = 20;
const LABEL_W = 176;
const LANE_COLORS = ["#003E87", "#0E7490", "#7C3AED", "#B45309", "#15803D", "#BE123C"];
const REASON_TINT = { weekend: "rgba(100,116,139,0.10)", holiday: "rgba(3,62,135,0.12)" };

const parseISO = (s) => new Date(`${(s || "").slice(0, 10)}T00:00:00`);
const dayDiff = (a, b) => Math.round((parseISO(b) - parseISO(a)) / 86400000);
const fmtShort = (s) => parseISO(s).toLocaleDateString(undefined, { month: "short", day: "numeric" });
const fmtLong = (s) => parseISO(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

function WorkingDatePicker({ value, minIso, workingDates, onPick, disabled, testid }) {
  const [open, setOpen] = useState(false);
  const options = useMemo(
    () => (workingDates || []).filter((d) => !minIso || d >= minIso),
    [workingDates, minIso]
  );
  return (
    <div className="relative inline-block">
      <button type="button" disabled={disabled} onClick={() => setOpen((o) => !o)} data-testid={testid}
        className="h-7 px-2 rounded border border-slate-200 text-[11px] font-mono text-slate-700 hover:border-[#003E87] disabled:opacity-50 flex items-center gap-1">
        {value ? fmtShort(value) : "—"} <ChevronDown className="w-3 h-3 text-slate-400" />
      </button>
      {open && !disabled && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-1 max-h-56 w-40 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg py-1" data-testid={`${testid}-list`}>
            {options.length === 0 && <div className="px-2 py-1 text-[11px] text-slate-400">No valid working day</div>}
            {options.map((d) => (
              <button key={d} onClick={() => { onPick(d); setOpen(false); }}
                className={`block w-full text-left px-2 py-1 text-[11px] font-mono hover:bg-slate-50 ${d === value ? "text-[#003E87] font-semibold" : "text-slate-600"}`}>
                {fmtLong(d)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function ScheduleTab({ bid, computed, updateBid, readOnly }) {
  const [sched, setSched] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drag, setDrag] = useState(null);      // {name, mode, startX, dx}
  const dragRef = useRef(null);

  const fetchSched = useCallback(async () => {
    try {
      const r = await api.get(`/bids/${bid.bid_id}/schedule`);
      setSched(r.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [bid.bid_id]);

  useEffect(() => { fetchSched(); }, [fetchSched, bid.scope_groups, computed]);

  const laneColor = useCallback((lane) => LANE_COLORS[(lane || 0) % LANE_COLORS.length], []);

  const isoToWorking = useMemo(() => {
    const m = {};
    (sched?.working_dates || []).forEach((d, i) => { m[d] = i; });
    return m;
  }, [sched]);

  // snap an arbitrary calendar iso to the nearest working date at or after it (else at or before)
  const snapWorking = useCallback((iso) => {
    const wd = sched?.working_dates || [];
    if (!wd.length) return iso;
    for (let i = 0; i < wd.length; i++) if (wd[i] >= iso) return wd[i];
    return wd[wd.length - 1];
  }, [sched]);

  const setGroup = useCallback((name, mutate) => {
    const groups = (bid.scope_groups || []).map((g) => (g.name === name ? mutate({ ...g }) : g));
    updateBid && updateBid({ scope_groups: groups });
  }, [bid.scope_groups, updateBid]);

  const saveBaseline = async () => {
    const label = window.prompt("Name this baseline (optional):", "") ?? "";
    try { await api.post(`/bids/${bid.bid_id}/schedule/baseline${label.trim() ? `?label=${encodeURIComponent(label.trim())}` : ""}`); toast.success("Baseline saved — slippage now tracked against it"); fetchSched(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save baseline"); }
  };
  const activateBaseline = async (id) => { try { await api.put(`/bids/${bid.bid_id}/schedule/baseline/${id}/activate`); fetchSched(); } catch { toast.error("Could not switch baseline"); } };
  const deleteBaseline = async (id) => { try { await api.delete(`/bids/${bid.bid_id}/schedule/baseline/${id}`); toast.success("Baseline removed"); fetchSched(); } catch { toast.error("Could not remove baseline"); } };
  const SlipChip = ({ n }) => {
    if (n == null || n === 0) return <span className="text-[10px] text-emerald-600 font-medium">on plan</span>;
    const late = n > 0;
    return <span className={`text-[10px] font-semibold ${late ? "text-rose-600" : "text-emerald-600"}`}>{late ? `+${n}d late` : `${-n}d early`}</span>;
  };

  // ---- drag handlers (move body -> lag ; resize right edge -> compress via fit-schedule) ----
  const onBarDown = (e, t, mode) => {
    if (readOnly) return;
    e.preventDefault(); e.stopPropagation();
    const start = { name: t.name, mode, startX: e.clientX, dx: 0, t };
    dragRef.current = start; setDrag({ ...start });
    const move = (ev) => { dragRef.current = { ...dragRef.current, dx: ev.clientX - start.startX }; setDrag({ ...dragRef.current }); };
    const up = () => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
      commitDrag(dragRef.current); dragRef.current = null; setDrag(null);
    };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
  };

  const commitDrag = (d) => {
    if (!d || !sched) return;
    const deltaDays = Math.round(d.dx / PX);
    if (deltaDays === 0) return;
    const t = d.t;
    if (d.mode === "move") {
      const newStartCal = new Date(parseISO(t.start_date).getTime() + deltaDays * 86400000).toISOString().slice(0, 10);
      const snapped = snapWorking(newStartCal < sched.project_start ? sched.project_start : newStartCal);
      const newIdx = isoToWorking[snapped];
      if (newIdx == null) return;
      const lag = Math.max(0, Math.round(newIdx - t.base_es));
      setGroup(t.name, (g) => ({ ...g, lag_days: lag }));
      toast.success(`${t.name} starts ${fmtLong(snapped)}${lag ? ` (lag ${lag}d)` : ""}`);
    } else {
      const newEndCal = new Date(parseISO(t.end_date).getTime() + deltaDays * 86400000).toISOString().slice(0, 10);
      const startIdx = isoToWorking[t.start_date] ?? Math.round(t.es);
      // snap end to a working day <= newEndCal
      const wd = sched.working_dates;
      let endIdx = startIdx;
      for (let i = 0; i < wd.length; i++) { if (wd[i] <= newEndCal) endIdx = i; else break; }
      const newWD = Math.max(1, endIdx - startIdx + 1);
      setGroup(t.name, (g) => ({ ...g, crew: { ...(g.crew || {}), target_work_days: newWD, days_override: undefined } }));
      toast.success(`${t.name} compressed to ${newWD} work-days — adding crews to hold the date`);
    }
  };

  const togglePred = (t, other) => {
    if (readOnly || other === t.name) return;
    const cur = new Set((bid.scope_groups || []).find((g) => g.name === t.name)?.predecessors || (t.auto_pred ? t.preds : []));
    cur.has(other) ? cur.delete(other) : cur.add(other);
    setGroup(t.name, (g) => ({ ...g, predecessors: [...cur] }));
  };

  if (loading && !sched) {
    return <div className="max-w-[1800px] w-full mx-auto px-6 py-16 text-center text-sm text-slate-400" data-testid="schedule-loading">Building schedule…</div>;
  }
  const tasks = sched?.tasks || [];
  if (!tasks.length) {
    return (
      <div className="max-w-[1800px] w-full mx-auto px-6 py-12 text-center" data-testid="schedule-empty">
        <CalendarRange className="w-8 h-8 text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-500">No scheduled scopes yet. Build a crew on a scope group in the Estimating tab and its bar appears here on the calendar.</p>
      </div>
    );
  }

  const { project_start, project_end, calendar_days } = sched;
  const width = Math.max(calendar_days, 1) * PX;
  const barLeft = (iso) => Math.max(0, dayDiff(project_start, iso)) * PX;
  const barWidth = (a, b) => Math.max(PX, (dayDiff(a, b) + 1) * PX);
  const rowY = (i) => i * ROW_H + (ROW_H - BAR_H) / 2;
  const taskIndex = {}; tasks.forEach((t, i) => { taskIndex[t.name] = i; });

  // month header ticks
  const months = [];
  {
    let d = parseISO(project_start); d.setDate(1);
    const end = parseISO(project_end);
    while (d <= end) {
      const iso = d.toISOString().slice(0, 10);
      months.push({ x: Math.max(0, dayDiff(project_start, iso)) * PX, label: d.toLocaleDateString(undefined, { month: "short", year: "2-digit" }) });
      d.setMonth(d.getMonth() + 1);
    }
  }

  const Stat = ({ icon: Icon, label, value, tint }) => (
    <div className="rounded-lg border border-slate-100 px-4 py-3 flex items-center gap-3" data-testid={`schedule-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <Icon className="w-5 h-5" style={{ color: tint }} />
      <div><div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
        <div className="text-base font-mono font-semibold text-slate-800">{value}</div></div>
    </div>
  );

  return (
    <div className="max-w-[1800px] w-full mx-auto px-4 sm:px-6 py-6" data-testid="schedule-tab">
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-display text-2xl font-bold tracking-tight text-black flex items-center gap-2">
          <CalendarRange className="w-5 h-5 text-[#003E87]" /> Schedule
        </h1>
        <div className="flex items-center gap-3">
          {!readOnly && <button onClick={saveBaseline} className="text-xs text-[#003E87] hover:underline flex items-center gap-1.5" data-testid="schedule-save-baseline">
            <Save className="w-3.5 h-3.5" /> Save baseline
          </button>}
          <button onClick={async () => {
            try {
              const res = await api.get(`/bids/${bid.bid_id}/programme/export`, { responseType: "blob" });
              const url = URL.createObjectURL(res.data); const a = document.createElement("a");
              a.href = url; a.download = "estimate_schedule.xlsx"; a.click(); URL.revokeObjectURL(url);
            } catch { toast.error("Could not export schedule"); }
          }} className="text-xs text-[#003E87] hover:underline flex items-center gap-1.5" data-testid="schedule-export">
            <Download className="w-3.5 h-3.5" /> Export (Excel)
          </button>
        </div>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Starts {fmtLong(project_start)} · finishes <span className="font-medium text-slate-700">{fmtLong(project_end)}</span>.
        Drag a bar to move it, or drag its right edge in to compress — crews are added automatically to hold the date. Weekends and US holidays are skipped; weather is already built into each scope's productivity (so it's not double-counted here).
      </p>
      {tasks.some((t) => t.block) && (
        <p className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4" data-testid="sched-standdown-note">
          <b>Stand-down days carried in this schedule:</b> {tasks.filter((t) => t.block).map((t) => `${t.name.replace(/ \(stand-down\)$/, "")} — ${t.wd} work-day${t.wd === 1 ? "" : "s"}`).join("; ")}.
          {" "}Shown as shaded days on the chart; the "weather" chips below are the same thing in short.
        </p>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
        <Stat icon={CalendarRange} label="Duration" value={`${calendar_days} days`} tint="#003E87" />
        <Stat icon={Clock} label="Work days" value={sched.project_work_days} tint="#0E7490" />
        <Stat icon={Users} label="Peak crew" value={sched.peak_crew} tint="#B45309" />
        <Stat icon={Zap} label="Critical" value={sched.critical_count} tint="#E11D48" />
        <Stat icon={GitBranch} label="Scopes" value={tasks.length} tint="#64748B" />
      </div>

      {sched.baseline && (
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-600 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="schedule-baseline-summary">
          <span className="font-semibold text-slate-500 uppercase tracking-wide text-[10px]">Baseline</span>
          {sched.baselines?.length > 1 ? (
            <select value={sched.baseline.id || ""} onChange={(e) => activateBaseline(e.target.value)} data-testid="baseline-select" className="text-[11px] border border-slate-200 rounded px-1 py-0.5 bg-white text-slate-700">
              {sched.baselines.map((b) => <option key={b.id} value={b.id}>{b.label || fmtLong(b.saved_at)}</option>)}
            </select>
          ) : <span className="font-medium text-slate-700">{sched.baseline.label || fmtLong(sched.baseline.saved_at)}</span>}
          <span>finish {fmtLong(sched.baseline.project_end)}</span>
          <span className="text-slate-300">·</span>
          <span>now {fmtLong(sched.project_end)}</span>
          <SlipChip n={sched.baseline.end_slip} />
          {sched.baseline.saved_by && <span className="text-slate-400 text-[10px]">by {sched.baseline.saved_by}</span>}
          {!readOnly && <button onClick={() => deleteBaseline(sched.baseline.id)} data-testid="schedule-delete-baseline" className="text-[10px] text-slate-400 hover:text-rose-600 ml-auto">remove</button>}
        </div>
      )}

      {sched.baseline && (() => {
        const movers = (tasks || []).filter((t) => t.end_slip != null && t.end_slip !== 0).sort((a, b) => Math.abs(b.end_slip) - Math.abs(a.end_slip)).slice(0, 4);
        if (!movers.length) return null;
        return (
          <div className="mb-4 rounded-lg border border-slate-100 px-3 py-2" data-testid="baseline-drivers">
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Biggest movers vs baseline</div>
            <div className="flex flex-wrap gap-2">{movers.map((m) => (
              <span key={m.name} className="text-[11px] px-2 py-0.5 rounded border border-slate-200 bg-white" data-testid={`baseline-mover-${m.name}`}>{m.name} <b className={m.end_slip > 0 ? "text-rose-600" : "text-emerald-600"}>{m.end_slip > 0 ? `+${m.end_slip}d` : `${m.end_slip}d`}</b></span>
            ))}</div>
          </div>
        );
      })()}

      {sched.warnings?.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800 flex items-start gap-2" data-testid="schedule-warnings">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>{sched.warnings.map((w, i) => <div key={i}>{w}</div>)}</div>
        </div>
      )}

      <div className="flex items-center gap-4 mb-2 text-[10px] text-slate-400">
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ background: REASON_TINT.weekend }} /> weekend</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ background: REASON_TINT.holiday }} /> holiday</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-1.5 rounded-sm bg-rose-500" /> critical path</span>
      </div>

      {/* ---- Gantt ---- */}
      <div className="border border-slate-200 rounded-lg overflow-auto" data-testid="schedule-gantt">
        <div className="relative" style={{ width: LABEL_W + width, minWidth: "100%" }}>
          {/* month header */}
          <div className="relative h-7 border-b border-slate-100 bg-slate-50/60" style={{ marginLeft: LABEL_W }}>
            {months.map((m, i) => (
              <div key={i} className="absolute top-0 h-7 border-l border-slate-200 pl-1 text-[10px] text-slate-500 leading-7" style={{ left: m.x }}>{m.label}</div>
            ))}
          </div>
          <div className="relative" style={{ height: tasks.length * ROW_H }}>
            {/* left labels */}
            <div className="absolute top-0 left-0 z-20 bg-white border-r border-slate-100" style={{ width: LABEL_W, height: tasks.length * ROW_H }}>
              {tasks.map((t, i) => (
                <div key={t.name} className="flex items-center gap-1 px-2 text-[11px] text-slate-600 truncate" style={{ height: ROW_H }} title={t.name}>
                  {t.critical && <Zap className="w-3 h-3 text-rose-500 shrink-0" />}<span className="truncate">{t.block ? (t.kind === "weather" ? "weather" : (t.kind || "stand-down")) : t.name}</span>
                </div>
              ))}
            </div>
            {/* timeline area */}
            <div className="absolute top-0" style={{ left: LABEL_W, width }}>
              {/* blocked-day shading */}
              {(sched.blocked || []).map((b, i) => {
                const x = dayDiff(project_start, b.date) * PX;
                if (x < 0 || x > width) return null;
                return <div key={i} className="absolute top-0" style={{ left: x, width: PX, height: tasks.length * ROW_H, background: REASON_TINT[b.reason] || REASON_TINT.weekend }} />;
              })}
              {/* dependency arrows */}
              <svg className="absolute top-0 left-0 pointer-events-none" width={width} height={tasks.length * ROW_H} style={{ overflow: "visible" }}>
                <defs>
                  <marker id="arr" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                    <path d="M0,0 L6,3 L0,6 Z" fill="#94a3b8" />
                  </marker>
                </defs>
                {tasks.map((t) => (t.preds || []).map((p) => {
                  const pt = tasks[taskIndex[p]]; if (!pt) return null;
                  const x1 = barLeft(pt.end_date) + barWidth(pt.start_date, pt.end_date);
                  const y1 = rowY(taskIndex[p]) + BAR_H / 2;
                  const x2 = barLeft(t.start_date);
                  const y2 = rowY(taskIndex[t.name]) + BAR_H / 2;
                  const midx = Math.max(x1 + 8, x2 - 8);
                  return <path key={`${p}->${t.name}`} d={`M${x1},${y1} L${midx},${y1} L${midx},${y2} L${x2},${y2}`} fill="none" stroke="#94a3b8" strokeWidth="1.3" markerEnd="url(#arr)" />;
                }))}
              </svg>
              {/* baseline ghost bars */}
              {tasks.map((t, i) => t.baseline_start ? (
                <div key={`ghost-${t.name}`} className="absolute rounded border border-dashed border-slate-400/70 pointer-events-none"
                  style={{ left: barLeft(t.baseline_start), top: rowY(i) - 3, width: barWidth(t.baseline_start, t.baseline_end), height: BAR_H + 6 }}
                  title={`Baseline: ${fmtLong(t.baseline_start)} → ${fmtLong(t.baseline_end)}`} data-testid={`gantt-baseline-${t.name}`} />
              ) : null)}
              {/* bars */}
              {tasks.map((t, i) => {
                const isDragging = drag && drag.name === t.name;
                const ddx = isDragging ? drag.dx : 0;
                const baseLeft = barLeft(t.start_date);
                const baseW = barWidth(t.start_date, t.end_date);
                const left = drag?.mode === "move" && isDragging ? baseLeft + ddx : baseLeft;
                const w = drag?.mode === "resize" && isDragging ? Math.max(PX, baseW + ddx) : baseW;
                const col = laneColor(t.lane);
                return (
                  <div key={t.name} className="absolute rounded flex items-center px-2 text-[10px] font-medium select-none group"
                    style={{ left, top: rowY(i), width: w, height: BAR_H, background: col, color: t.lane === 4 || false ? "#fff" : "#fff", outline: t.critical ? "2px solid #E11D48" : "none", outlineOffset: "-1px", cursor: readOnly ? "default" : "grab", opacity: isDragging ? 0.85 : 1 }}
                    title={`${t.name}\n${fmtLong(t.start_date)} → ${fmtLong(t.end_date)}\n${t.wd} work-days · ${t.crews ?? "—"} crew · peak ${t.peak_people ?? "—"} · float ${t.float_days}d\n${money(t.labor_cost || 0)} labor`}
                    data-testid={`gantt-bar-${t.name}`}
                    onPointerDown={(e) => onBarDown(e, t, "move")}>
                    <span className="truncate pointer-events-none">{t.wd}d{t.crews ? ` · ${t.crews}×` : ""}</span>
                    {!readOnly && (
                      <span onPointerDown={(e) => onBarDown(e, t, "resize")} data-testid={`gantt-resize-${t.name}`}
                        className="absolute right-0 top-0 h-full w-2 cursor-ew-resize bg-black/20 rounded-r" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ---- Task detail / dependencies ---- */}
      <div className="mt-6" data-testid="schedule-tasks">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5"><GitBranch className="w-4 h-4 text-[#003E87]" /> Scopes &amp; dependencies</div>
        <div className="space-y-2">
          {tasks.map((t) => (
            <div key={t.name} className="rounded-lg border border-slate-100 p-3" data-testid={`sched-row-${t.name}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <span className="text-xs font-medium text-slate-700 flex items-center gap-1">
                  {t.critical && <Zap className="w-3 h-3 text-rose-500" />}{t.name}
                  <span className="text-[10px] text-slate-400">· {t.wd}d · {t.crews ?? "—"} crew · peak {t.peak_people ?? "—"} · float {t.float_days}d</span>
                  {sched.baseline && t.end_slip != null && <SlipChip n={t.end_slip} />}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-400">start</span>
                  <WorkingDatePicker value={t.start_date} minIso={sched.working_dates?.[Math.max(0, Math.round(t.base_es))]}
                    workingDates={sched.working_dates} disabled={readOnly} onPick={(d) => {
                      const idx = isoToWorking[d]; if (idx == null) return;
                      const lag = Math.max(0, Math.round(idx - t.base_es));
                      setGroup(t.name, (g) => ({ ...g, lag_days: lag }));
                    }} testid={`sched-start-${t.name}`} />
                  <span className="text-[10px] text-slate-400">lag</span>
                  <input key={`${t.name}-${t.lag}`} type="number" min="0" defaultValue={t.lag} disabled={readOnly} data-testid={`sched-lag-${t.name}`}
                    onBlur={(e) => setGroup(t.name, (g) => ({ ...g, lag_days: Math.max(0, Number(e.target.value || 0)) }))}
                    className="h-7 w-14 rounded border border-slate-200 px-1 text-[11px] font-mono text-right disabled:opacity-50" />d
                </div>
              </div>
              {!readOnly && (
                <div className="flex flex-wrap gap-1.5">
                  {tasks.filter((o) => o.name !== t.name && !o.block).map((o) => {
                    const explicit = (bid.scope_groups || []).find((g) => g.name === t.name)?.predecessors || [];
                    const on = (explicit.length ? explicit : t.preds).includes(o.name);
                    const implicit = t.auto_pred && !explicit.length && t.preds.includes(o.name);
                    return (
                      <button key={o.name} onClick={() => togglePred(t, o.name)} data-testid={`dep-chip-${t.name}-${o.name}`}
                        className={`px-2 py-0.5 rounded text-[10px] border ${on ? (implicit ? "bg-slate-100 border-slate-200 text-slate-500 italic" : "bg-[#003E87] border-[#003E87] text-white") : "border-slate-200 text-slate-400 hover:border-[#003E87]"}`}
                        title={implicit ? "auto-sequenced (click to lock as an explicit link)" : "finish-to-start predecessor"}>
                        {o.name}{implicit ? " (auto)" : ""}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
        <p className="text-[11px] text-slate-400 mt-3">Click a scope chip to make it a finish-to-start predecessor (this scope starts after that one finishes + lag). The date picker only offers valid working days on or after the earliest possible start.</p>
      </div>
    </div>
  );
}
